/**
 * Asset View Component
 *
 * Full-screen view for viewing/managing asset details.
 * Shows asset preview on the left and tabbed content on the right.
 */

import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import type { AlertTypes, DropdownDirection, ZprSelectCustomEvent } from '@zappar/zpr-ui-components-stencil';
import { IAsset, ProviderRegistry, ZapworksProvider } from '../providers';
import { useAssetNameEdit, useWorkflowStatuses } from '../hooks';
import { useAssetActions } from '../context/AssetActionsContext';
import { AssetSource, MediaType } from '../types';
import { useEchoContext } from '../echo/EchoContext';
import { Client, IAnalyticsSummary, IFileVersionEntry, IPublicLink } from '@zappar/zw-api-client';

import { ArrowBackIcon, ProcessingIcon, DownloadIcon, PenIcon, CrossIcon } from '../../../icons';
import { ZprAlert, ZprSelect, ZprSelectOption, ZprSelectOptionGroup, ZprTabs, ZprInlineInput } from '@zpr-stencil';
import { downloadAsset, formatDate, triggerBlobDownload } from '../utils';
import getAcceptedFileTypes from '../utils';
import { useMode } from '../context/ModeContext';
import { AssetPreview } from './AssetPreview';
import { AssetInfoAccordion } from './AssetInfoAccordion';
import { AssetDetailActions } from './AssetDetailActions';
import { LabelsAccordion } from './LabelsAccordion';
import { PublicLinkTab } from './PublicLinkTab';
import { CommentsTab } from './CommentsTab';
import { useTheme } from '../context/ThemeContext';

export interface AssetViewProps {
  /** Zapworks API client (used for auth/env when connecting to Echo) */
  zwApiClient: Client;
  /** Provider registry containing the Zapworks provider */
  registry: ProviderRegistry;
  /** The asset being viewed */
  asset: IAsset;
  /** Currently active source */
  activeSource: AssetSource;
  /** Callback to go back to the previous view */
  onBack: () => void;
  /** Callback to handle status changes */
  onStatusChange?: (newStatus: string) => void;
  /** Callback to handle file uploads for new versions */
  onUploadNewVersion?: (asset: IAsset, file: File) => void;
  /** Callback to handle asset deletion */
  onDelete?: (assetId: string, asset: IAsset) => Promise<boolean>;
  /** Callback to handle asset restoration */
  onRestore?: (assetId: string) => Promise<void>;
  /** Accepted file types for uploads */
  acceptedFileTypes?: MediaType[];
  /** Callback to navigate to the full analytics dashboard */
  onViewAnalytics?: () => void;
  /** Callback to handle public link creation */
  onPublicLinkCreated?: () => void;
  /** Callback invoked when a public link operation fails (e.g. duplicate slug) */
  onPublicLinkError?: (message: string) => void;
  refreshKey?: number; // Optional key to force remounting the component for refreshing data
  environmentImage?: string; // Optional environment image URL for ModelViewer previews
  disableEditName?: boolean; // Optional flag to disable editing the asset name
}

export const AssetView = ({
  zwApiClient,
  asset,
  activeSource,
  onBack,
  registry,
  onUploadNewVersion,
  onStatusChange,
  onDelete,
  onRestore,
  acceptedFileTypes = [],
  onViewAnalytics,
  onPublicLinkCreated,
  onPublicLinkError,
  refreshKey,
  environmentImage,
  disableEditName,
}: AssetViewProps) => {
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [modelSrc, setModelSrc] = useState<string>('');
  // Historical version currently overriding the preview (null = show the live file)
  const [previewedVersion, setPreviewedVersion] = useState<{ uid: string; version: number; url: string } | null>(null);
  const [previewVersionError, setPreviewVersionError] = useState<string | null>(null);
  const [hasLoadedCommentsTab, setHasLoadedCommentsTab] = useState(false);
  const [hasLoadedEmbedTab, setHasLoadedEmbedTab] = useState(false);
  const [embedCode, setEmbedCode] = useState<string | undefined>(undefined);
  const [showEmbedPreview, setShowEmbedPreview] = useState(false);
  // Bumped after a (re)publish so the Overview tab refetches versions and flags
  // the correct published version.
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);
  const { premium, useNestleFeatures, permissions } = useMode();
  const { workspaceId } = useAssetActions();

  const handleTabsChange = (event: CustomEvent<{ id: number }>) => {
    if (event.detail.id === 1) setHasLoadedCommentsTab(true);
    if (event.detail.id === 2) setHasLoadedEmbedTab(true);
    setShowEmbedPreview(event.detail.id === 2); // Show embed preview only when Publish tab is active
  };

  const [selectedAsset, setSelectedAsset] = useState<IAsset>(asset);

  // undefined = loading, null = no link exists, IPublicLink = link exists
  const [publicLink, setPublicLink] = useState<IPublicLink | null | undefined>(undefined);

  const isStale = useMemo(() => {
    if (!publicLink) return false;
    if (publicLink.publishedVersion == null) return false;
    return publicLink.publishedVersion < asset.getVersion();
  }, [publicLink, asset, versionsRefreshKey]);

  useEffect(() => {
    if (!workspaceId || !premium) return;
    asset.getPublicLink?.()
      .then(link => {
        setPublicLink(link);
      })
      .catch(() => {
        setPublicLink(null);
      });
  }, [asset, workspaceId, premium, versionsRefreshKey]);


  useEffect(() => {
    if (activeSource === 'explore') return;
    let cancelled = false;
    const fetchAssetData = async () => {
      const zapworksProvider = registry.get('zapworks') as ZapworksProvider | undefined;
      const newAsset = await zapworksProvider?.getAssetById(asset.getId(), asset.getWorkspaceId() ?? '');
      if (!cancelled && newAsset) {
        setSelectedAsset(newAsset);
      }
    };
    fetchAssetData();
    return () => { cancelled = true; };
  }, [asset, refreshKey]);

  useEffect(() => {
    const loadPreview = async () => {
      const url = await selectedAsset.getPreviewUrl({ width: 500, height: 500 }, false);
      setPreviewUrl(url);
      const modelUrl = await selectedAsset.getDownloadUrl(false);
      setModelSrc(modelUrl);
    };
    loadPreview();
  }, [selectedAsset, refreshKey]);

  // Object URLs are revoked as soon as they are replaced, and on unmount. The URL is
  // tracked in a ref rather than an effect on `previewedVersion` so that StrictMode's
  // double-invoked cleanup can't revoke a URL that is still on screen.
  const previewedVersionUrl = useRef<string | null>(null);
  // Monotonic token so a slow download can tell it has been superseded
  const latestPreviewRequest = useRef(0);
  const showVersionPreview = (next: { uid: string; version: number; url: string } | null) => {
    latestPreviewRequest.current++;
    if (previewedVersionUrl.current) URL.revokeObjectURL(previewedVersionUrl.current);
    previewedVersionUrl.current = next?.url ?? null;
    setPreviewedVersion(next);
  };
  useEffect(() => () => {
    if (previewedVersionUrl.current) URL.revokeObjectURL(previewedVersionUrl.current);
    previewedVersionUrl.current = null;
  }, []);

  // A new asset (e.g. after uploading a new version) always starts on the live file
  useEffect(() => {
    showVersionPreview(null);
    setPreviewVersionError(null);
  }, [selectedAsset]);

  /**
   * Swaps the preview over to a historical version. There is no URL endpoint for
   * archived versions, so the bytes are streamed and wrapped in an object URL.
   * Picking the current version (or the version already shown) returns to the live file.
   */
  const handlePreviewVersion = async (version: IFileVersionEntry) => {
    setPreviewVersionError(null);
    const request = ++latestPreviewRequest.current;
    if (version.isCurrent || previewedVersion?.uid === version.uid) {
      showVersionPreview(null);
      return;
    }
    try {
      // TODO: Replace this with a proper preview URL endpoint when available, rather than downloading the whole file
      const { blob } = await selectedAsset.downloadFileVersion(version.uid);
      // A newer request (or a return to the live file) superseded this download
      if (request !== latestPreviewRequest.current) return;
      showVersionPreview({ uid: version.uid, version: version.version, url: URL.createObjectURL(blob) });
    } catch (error) {
      if (request !== latestPreviewRequest.current) return;
      console.error('Failed to load version preview:', error);
      setPreviewVersionError(`Failed to load preview for v${version.version}`);
    }
  };

  const isModel = selectedAsset.getMediaType() === MediaType.Model3D;

  const handleUpload = (file: File) => {
    if (onUploadNewVersion) {
      onUploadNewVersion(selectedAsset, file);
    }
  };

  const renderOverviewTab = () => (
    <OverviewTab premium={premium} useNestleFeatures={useNestleFeatures} registry={registry} asset={selectedAsset} activeSource={activeSource} onUpload={handleUpload} acceptedFileTypes={acceptedFileTypes} onStatusChange={onStatusChange} onDelete={onDelete} onBack={onBack} onRestore={onRestore} onViewAnalytics={onViewAnalytics} versionsRefreshKey={versionsRefreshKey} onPreviewVersion={handlePreviewVersion} previewedVersionUid={previewedVersion?.uid ?? null} disableEditName={disableEditName} />
  )

  return (
    <div className="asset-view">
      <div className="asset-view__preview">
        <header>
          <button
            className="btn btn-ghost p0"
            onClick={onBack}
            aria-label="Close asset view"
          >
            <ArrowBackIcon />
            Back
          </button>
        </header>
        <div className="asset-view__preview-container">
          {!showEmbedPreview && previewVersionError ? (
            <div className='alert-container' key='preview-version-error'>
              <ZprAlert type={"error" as AlertTypes} className='alert-plain alert' dismissible={false}>
                {previewVersionError}
              </ZprAlert>
            </div>
          ) : !showEmbedPreview && previewedVersion ? (
            <div className='alert-container' key='previewing-version'>
              <ZprAlert type={"info" as AlertTypes} className='alert-plain alert' dismissible={false}>
                {`Previewing v${previewedVersion.version}`}
                <button
                  type="button"
                  className="btn btn-link btn-small p0"
                  onClick={() => showVersionPreview(null)}
                >
                  Back to latest
                </button>
              </ZprAlert>
            </div>
          ) : premium && isStale && publicLink && (
            <div className='alert-container' key='stale-published-version'>
              <ZprAlert type={"warning" as AlertTypes} className='alert-plain alert' dismissible={false}>
                Older version published – Publish again to update asset
              </ZprAlert>
            </div>
          )}
          <AssetPreview
            name={selectedAsset.getName()}
            previewUrl={(!showEmbedPreview &&!isModel && previewedVersion?.url) || previewUrl}
            mediaType={selectedAsset.getMediaType()}
            modelSrc={!showEmbedPreview && previewedVersion?.url || modelSrc}
            isInteractive={true}
            embedCode={embedCode}
            showEmbedPreview={showEmbedPreview}
            key={`${refreshKey}-${previewedVersion?.uid ?? 'current'}`}
            environmentImage={environmentImage}
            id={selectedAsset.getId()}
            showFontDetails={true}
          />
        </div>
      </div>

      <div className="asset-view__body">
        <div className="asset-view__panel-section">
          {premium ? (
            <div className="asset-view__tabs">
              <ZprTabs onTabsChange={handleTabsChange}>
                <div tab-header="Overview"><div className="asset-view__tabs-content">{renderOverviewTab()}</div></div>
                <div tab-header="Comments / Feed"><div className="asset-view__tabs-content">{hasLoadedCommentsTab ? <CommentsTab asset={selectedAsset} zwApiClient={zwApiClient} /> : null}</div></div>
                {activeSource !== 'trash' && (
                  <div tab-header="Publish"><div className="asset-view__tabs-content">{hasLoadedEmbedTab ? <PublicLinkTab asset={selectedAsset} zwApiClient={zwApiClient} onEmbedCodeBuilt={setEmbedCode} havePermissionToPublish={permissions?.canPublish === true} onPublicLinkCreated={onPublicLinkCreated} onPublished={() => setVersionsRefreshKey(k => k + 1)} onError={onPublicLinkError} /> : null}</div></div>
                )}
              </ZprTabs>
            </div>
          ) : (
            <div className="asset-view__tabs-content">
              {renderOverviewTab()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OverviewTab = ({ asset, registry, activeSource, acceptedFileTypes, onUpload, onStatusChange, onDelete, onRestore, onBack, premium, useNestleFeatures, onViewAnalytics, versionsRefreshKey, onPreviewVersion, previewedVersionUid, disableEditName }: {
  asset: IAsset; registry: ProviderRegistry; activeSource: AssetSource;
  acceptedFileTypes?: MediaType[]; onUpload?: (file: File) => void; onStatusChange?: (newStatus: string) => void; onDelete?: (assetId: string, asset: IAsset) => Promise<boolean>; onRestore?: (assetId: string) => Promise<void>; onBack?: () => void; premium: boolean; useNestleFeatures: boolean; onViewAnalytics?: () => void; versionsRefreshKey?: number; onPreviewVersion?: (version: IFileVersionEntry) => Promise<void>; previewedVersionUid?: string | null; disableEditName?: boolean;
}) => {
  const { zapworksProvider, workspaceId, onAddToWorkspaceAssets } = useAssetActions();
  const { statuses: workflowStatuses } = useWorkflowStatuses(zapworksProvider, workspaceId);
  const { isToolMode, permissions } = useMode();
  const theme = useTheme();
  const [allVersions, setAllVersions] = useState<any[]>([]);
  const { postSystemComment } = useEchoContext();
  const { editedName, isLoadingName, handleNameChange, handleSaveName, isSuccess, error } = useAssetNameEdit(asset, postSystemComment);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(true); // Hide delete confirmation when public link is not existing.
  const metadata = asset.getMetadata();
  const assetId = asset.getId();

  // Extract metadata fields
  const [workflowStatus, setWorkflowStatus] = useState<string>(metadata.workflowStatusId as string || '');

  useEffect(() => {
    setWorkflowStatus(asset.getMetadata().workflowStatusId as string || '');
  }, [asset]);

  useEffect(() => {
    if (!premium) return;
    let cancelled = false;
    const checkPublicLink = async () => {
      if (asset.getPublicLink) {
        const publicLink = await asset.getPublicLink();
        if (cancelled) return;
        setShowDeleteConfirmation(!!publicLink);
      }
    };
    checkPublicLink();
    return () => { cancelled = true; };
  }, [asset]);

  const canDelete = (metadata.canDelete as boolean) || false;
  const binned = (metadata.binned as boolean) || false;
  const canRestore = (metadata.canRestore as boolean) || false;

  const [serviceNowTicket, setServiceNowTicketValue] = useState<string>((metadata.serviceNowTicket as string) || '');
  const [isLoadingTicket, setIsLoadingTicket] = useState(false);
  const [ticketSuccess, setTicketSuccess] = useState(false);
  const [ticketError, setTicketError] = useState<string | null>(null);
  const { onAddToProject } = useAssetActions();

  const handleTicketChange = (newValue: string) => {
    setServiceNowTicketValue(newValue);
    setTicketSuccess(false);
    setTicketError(null);
  };

  const handleSaveTicket = async () => {
    setIsLoadingTicket(true);
    setTicketSuccess(false);
    setTicketError(null);
    try {
      await asset.setServiceNowTicket?.(serviceNowTicket || null);
      setTicketSuccess(true);
      setTimeout(() => setTicketSuccess(false), 5000);
    } catch {
      setTicketError('Failed to update ServiceNow ticket');
      setServiceNowTicketValue((metadata.serviceNowTicket as string | null) || '');
    } finally {
      setIsLoadingTicket(false);
    }
  };

  const [analyticsSummary, setAnalyticsSummary] = useState<IAnalyticsSummary | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const handleAddToWorkspaceAssets = async (_isAdded: boolean) => {
    if (onAddToWorkspaceAssets) {
      await onAddToWorkspaceAssets([asset]);
    }
  };

  const handleMoveToBin = async () => {
    if (onDelete) await onDelete(assetId, asset);
    if (onBack) onBack();
  };

  useEffect(() => {
    if (!premium) return;
    const loadAllVersions = async () => {
      const zapworksProvider = registry.get('zapworks') as ZapworksProvider | undefined;
      const versions = await zapworksProvider?.getFileHistory(asset.getWorkspaceId() ?? '', assetId);
      setAllVersions(versions || []);
    };
    loadAllVersions();
  }, [assetId, registry, versionsRefreshKey]);

  useEffect(() => {
    if (!premium || !workspaceId) return;
    setAnalyticsLoading(true);
    zapworksProvider?.getAnalyticsSummary(workspaceId, assetId)
      .then(data => setAnalyticsSummary(data))
      .catch(() => setAnalyticsSummary({ last7DaysViews: 0, totalViews: 0 }))
      .finally(() => setAnalyticsLoading(false));
  }, [assetId, workspaceId, zapworksProvider, premium]);

  const handleStatusChange = (newStatus: string) => {
    onStatusChange?.(newStatus);
    setWorkflowStatus(newStatus);
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onUpload) {
      onUpload(file);
      // Reset the input so the same file can be selected again
      event.target.value = '';
    }
  };

  return (
    <>
      <section>
        <ZprInlineInput
          name="asset-name"
          label="Asset name"
          readOnly={binned || disableEditName}
          disabled={binned || isLoadingName}
          value={editedName}
          onValueChange={e => handleNameChange(e.detail)}
          onEdit={handleSaveName}
          placeholder="Enter asset name"
          ariaLabel="Asset name"
          isLoading={isLoadingName}
          isSuccess={isSuccess}
          isError={!!error}
          errorMessage={error || 'Failed to update asset name'}
        />
        {premium && useNestleFeatures && (
          <>
            <ZprInlineInput
              label='ServiceNow ticket'
              name="service-now-ticket"
              readOnly={!(permissions && Object.values(permissions).every(val => val === true))}
              disabled={isLoadingTicket || !(permissions && Object.values(permissions).every(val => val === true)) }
              value={serviceNowTicket}
              onValueChange={e => handleTicketChange(e.detail)}
              onEdit={handleSaveTicket}
              isLoading={isLoadingTicket}
              isSuccess={ticketSuccess}
              isError={!!ticketError}
              copyable
              errorMessage={ticketError || 'Failed to update ServiceNow ticket'}
              placeholder="Asset request link (optional)"
              ariaLabel="Asset request link"
            />
          </>
        )}
      </section>
      <section>
        {/* AssetInfoAccordion */}
        <AssetInfoAccordion
          asset={asset}
        />
        {activeSource !== 'user-library' && activeSource !== 'explore' && (
          <LabelsAccordion
            asset={asset}
            isEditableAsset={!binned}
            canEditLabels={permissions?.canEditLabels === true}
            canAssignLabels={permissions?.canAssignLabels === true}
          />
        )}
      </section>
      {premium && (
        <>
          <section>
            <h4>Status</h4>
            <div className="asset-info-row">
              <label>Status</label>
              {permissions?.canChangeStatus === true ? (
                <ZprSelect
                  variant='status'
                  label={workflowStatuses.find(s => s.uid === workflowStatus)?.name || 'None'}
                  value={workflowStatus}
                  dropdownDirection={"left" as DropdownDirection}
                  onClick={(ev: ReactMouseEvent) => ev.stopPropagation()}
                  onSelectchanged={(e: ZprSelectCustomEvent<{ value: string; label: string }>) => handleStatusChange(e.detail.value)}
                  theme={theme}
                  dropdownZIndex="1001"
                >
                  <span slot='icon'><PenIcon width={16} height={16} /></span>
                  <button slot='delete' type="button" className="btn btn-ghost p0 btn-small" onClick={ev => {ev.stopPropagation(); handleStatusChange('');}}>
                    <CrossIcon width={16} height={16} />
                  </button>
                  <ZprSelectOptionGroup>
                    {workflowStatuses.map(s => (
                      <ZprSelectOption key={s.uid} value={s.uid} label={s.name} selected={s.uid === workflowStatus}>
                        {s.name}
                      </ZprSelectOption>
                    ))}
                  </ZprSelectOptionGroup>
                </ZprSelect>
              ) : workflowStatuses.find(s => s.uid === workflowStatus)?.name ?(
                <span className='status-label'>{workflowStatuses.find(s => s.uid === workflowStatus)?.name}</span>
              ) : (<span>None</span>)}
            </div>
          </section>
          <section>
            <h4>Analytics</h4>
            <div className="asset-info-row">
              <label>Last 7 days</label>
              {analyticsLoading ? (
                <ProcessingIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
              ) : (
                <span>{analyticsSummary?.last7DaysViews ?? 0}</span>
              )}
            </div>
            <div className="asset-info-row">
              <label>Total views</label>
              {analyticsLoading ? (
                <ProcessingIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
              ) : (
                <span>{analyticsSummary?.totalViews ?? 0}</span>
              )}
            </div>
            {onViewAnalytics && (
              <div>
                <button
                  type="button"
                  className="btn btn-small btn-link"
                  onClick={onViewAnalytics}
                >
                  View more
                </button>
              </div>
            )}
          </section>
          <section>
            {onUpload && (
              <label className={`btn btn-small btn-secondary ${permissions?.canUploadVersion !== true ? 'disabled' : ''}`} aria-label="Upload new version of asset">
                Upload new version
                <input
                  type="file"
                  accept={acceptedFileTypes?.map(type => getAcceptedFileTypes(type)).join(',')}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  aria-label="Select files to upload"
                  disabled={permissions?.canUploadVersion !== true}
                />
              </label>
            )}

            {allVersions.length > 0 && (
              <>
                <h4>Version history</h4>
                <ul className="version-history">
                  {allVersions.map((version, index) => (
                    <VersionHistoryItem
                      key={version.uid}
                      version={version}
                      index={index}
                      onPreview={onPreviewVersion}
                      isPreviewing={previewedVersionUid ? version.uid === previewedVersionUid : !!version.isCurrent}
                    />
                  ))}
                </ul>
              </>
            )}
          </section>
        </>
      )}
      <section>
        <h4>Actions</h4>
        <AssetDetailActions
          key={assetId}
          binned={binned}
          canDelete={canDelete}
          canRestore={canRestore}
          activeSource={activeSource}
          onMoveToBin={handleMoveToBin}
          onAddToWorkspaceAssets={() => handleAddToWorkspaceAssets(false)}
          onRestore={onRestore ? () => onRestore(assetId) : undefined}
          onDownload={() => downloadAsset(asset)}
          havePermissionToDelete={permissions?.canDeleteAsset === true}
          showDeleteConfirmation={showDeleteConfirmation}
        />
      </section>
      {isToolMode && (
        <section>
          <button
            className="btn btn-small btn-primary"
            type="button"
            aria-label="Add to project"
            onClick={() => onAddToProject?.(asset)}
          >
            Add to project
          </button>
        </section>
      )}
    </>
  )
};

const VersionHistoryItem = ({ version, index, onPreview, isPreviewing = false }: {
  version: IFileVersionEntry; index: number; onPreview?: (version: IFileVersionEntry) => Promise<void>; isPreviewing?: boolean;
}) => {
  const { zapworksProvider, workspaceId } = useAssetActions();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const handleDownload = async () => {
    if (isDownloading || !zapworksProvider) return;
    setIsDownloading(true);
    try {
      const asset = await zapworksProvider.getAssetById(version.fileId, workspaceId);
      const { blob, filename } = await asset.downloadFileVersion(version.uid);
      triggerBlobDownload(blob, filename);
    } catch (error) {
      console.error('Failed to download version:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePreviewVersion = async () => {
    if (isLoadingPreview || isPreviewing || !onPreview) return;
    setIsLoadingPreview(true);
    try {
      await onPreview(version);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  return (
    <li className="version-history-item">
      <div>
        <p>{version.filename}</p>
        {version.uploadedAt && (
          <span>
            {`${formatDate(new Date(version.uploadedAt))} – v${version.version}`}
            {version.isCurrent ? ' (latest)' : ''}
            {version.isPublished ? ' (published)' : ''}
          </span>
        )}
      </div>
      <div className="version-history-item__actions">
        {isPreviewing ? (
          <span className="version-history-item__previewing">Previewing</span>
        ) : (
          <button
            type="button"
            onClick={handlePreviewVersion}
            disabled={isLoadingPreview || !onPreview}
            aria-label={`Preview version ${index + 1} of asset`}
            className={`btn btn-ghost p0 ${isLoadingPreview ? 'processing' : ''}`}
          >
            Preview
          </button>
        )}

        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading}
          className={`btn btn-ghost p0 ${isDownloading ? 'processing' : ''}`}
          aria-label={`Download version ${index + 1} of asset`}
        >
          <DownloadIcon width={16} height={16} />
        </button>
      </div>
    </li>
  );
};

